<template>
  <div class="signature">
    <div class="signature__container container">
      <a
        class="signature__logo"
        href="https://www.koalect.com/"
        target="_blank"
        rel="noopener noreferrer"
      >
        <img
          :src="getImagePath('logo/koalect_raisenow_dark_grey_logo.png')"
          alt="Koalect"
        >
      </a>

      <nav class="nav">
        <ul class="nav__ul">
          <li
            class="nav__li"
            v-for="page in pages"
            :key="page.path"
          >
            <router-link
              class="link__color--lg-prim link--naked"
              :to="{ name: 'Page', params: { path: page.path, locale } }"
            >
              {{ translate(page.title) }}
            </router-link>
          </li>

          <li
            class="nav__li"
            v-if="client.pages.cookie_policy?.is_published"
          >
            <button
              class="link__color--lg-prim link--naked"
              @click="trigger('cookieBarRequested')"
            >
              {{ t('literal.cookie_preferences') }}
            </button>
          </li>

          <li class="nav__li">
            © <app-date
              :timestamp="new Date()"
              filter="yyyy"
            /> Koalect
          </li>
        </ul>

        <div class="signature__currency">
          <currency-switcher
            position-y="top"
          />
        </div>
      </nav>
    </div>

    <cookie-bar v-if="!allowedConsentManagementPlatform" />
  </div>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { useClientStore } from '@/stores/ClientStore'
import { useLanguageStore } from '@/stores/LanguageStore'
import { getImagePath } from '@/utils/utils'
import { useI18n } from '@/vendors/i18n'
import { useAllowedFeatures } from '@/composables/app/useAllowedFeatures'
import { useConsent } from '@/vendors/consent'

import CookieBar from '&/organisms/CookieBar'
import AppDate from '&/atoms/AppDate'
import CurrencySwitcher from '&/atoms/CurrencySwitcher'

const { locale } = storeToRefs(useLanguageStore())
const { getClient } = useClientStore()
const { translate, t } = useI18n()
const { allowedConsentManagementPlatform } = useAllowedFeatures()
const { trigger } = useConsent()

const { client } = getClient()

const pages = client.value.pages.all(['privacy-policy', 'terms-of-use']).filter(page => page.is_published && translate(page.title))
</script>

<style lang="scss" scoped>
.signature {
  position: relative;
  border-top: 1px solid $dw;
  padding: 18px 0;
  background-color: $color--bg;
  text-align: center;

  @include mq(sm) {
    padding: 24px 0;
  }

  &__container {
    display: flex;
    flex-direction: column;
    gap: 12px;
    align-items: center;
    justify-content: center;

    @include mq(md) {
      flex-direction: row;
      justify-content: space-between;
    }
  }

  &__logo {
    display: flex;
    justify-content: center;
    width: 118px;
  }

  &__currency {
    display: flex;
    justify-content: center;
    margin-top: 12px;

    @include mq(sm) {
      margin: 0;
    }
  }
}

.nav {
  @include mq(sm) {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  &__ul {
    @include mq(sm) {
      display: flex;
      align-items: center;
      flex: 1 1 auto;
      overflow-x: auto;
      scrollbar-color: $dw $color--bg;
      scrollbar-width: thin;
      margin-left: auto;
    }

    @include mq(md) {
      justify-content: flex-end;
    }
  }

  &__li {
    font-size: em(14px);
    color: $mg;
    padding: 3px 0;
    white-space: nowrap;

    @include mq(sm) {
      &:not(:first-child):before {
        content: '';
        display: inline-block;
        width: 1px;
        height: 16px;
        background: $vlg;
        border-radius: 50%;
        vertical-align: middle;
        margin: 0 6px;
      }
    }

    @include mq(md) {
      &:not(:first-child):before {
        margin: 0 12px;
      }
    }
  }
}

.modal {
  &__body {
    padding: 24px;

    @include mq(md) {
      padding: 36px 48px;
    }
  }
}
</style>
